import { Playbook } from '@/types/playbook/playbook';
import { Bot, CheckCircle, FileText, Star, TrendingUp, User } from 'lucide-react';

export const testimonialHarvester: Playbook = {
  meta: {
    id: 'testimonial-harvester',
    slug: 'testimonial-harvester',
    categoryId: 'gather-insights',
    name: 'The Testimonial Harvester',
    tagline: 'Turn happy customers into marketing.',
    description: 'Identify satisfied customers and automatically collect testimonials, reviews, and case studies for marketing purposes.',
    strategy: 'Identify satisfied customers and collect testimonials, reviews, and case studies automatically.',
    benefits: [
      'Automated testimonial collection',
      'Identify brand advocates',
      'Create marketing content',
      'Build social proof'
    ],
    targetAudience: ['Marketing teams', 'Customer success', 'Sales teams'],
    estimatedSetupTime: '20 minutes',
    difficulty: 'intermediate',
    tags: ['testimonials', 'case-studies', 'marketing-content', 'brand-advocates'],
    isFeatured: true
  },
  seo: {
    metaTitle: 'Testimonial Harvester Playbook - Automated Customer Success Stories',
    metaDescription: 'Automatically collect testimonials and case studies from satisfied customers with a Genie. Turn success into powerful marketing content.',
    keywords: ['testimonial collection', 'customer success stories', 'case studies', 'brand advocates', 'marketing automation']
  },
  demoConfig: {
    triggerType: 'delayed',
    delay: 3000,
    message: "🌟 Wow! You've achieved great results with our platform. Would you mind sharing your success story? We'd love to feature it!",
    position: 'center'
  },
  demoScript: [
    { speaker: 'agent', message: "🌟 Your usage shows amazing results! 50% improvement - that's fantastic!" },
    { speaker: 'user', message: "Yes, we're really happy with it" },
    { speaker: 'agent', message: "Would you mind sharing a brief testimonial? It helps others like you." },
    { speaker: 'user', message: "Sure, what should I include?" },
    { speaker: 'agent', message: "Just describe your challenge and how we helped. I can draft it with you!" }
  ],
  templateConfig: {
    firstMessage: "🌟 You're getting great results! Would you share your success story?",
    systemPrompt: "You are a testimonial-collecting Genie. Identify happy customers, gather success stories, and make it easy for them to provide testimonials. Be appreciative and make the process simple.",
    tools: ['success_metrics', 'testimonial_templates', 'review_platforms']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'Customer achieves success',
      description: 'System detects customer with high satisfaction scores or strong usage metrics'
    },
    {
      type: 'genie',
      icon: TrendingUp,
      genieIcon: 'genie-lamp',
      label: 'Identifies success opportunity',
      description: 'Genie recognizes positive customer journey and outcome metrics'
    },
    {
      type: 'action',
      icon: Bot,
      genieIcon: 'smile',
      label: 'Celebrates customer success',
      description: 'Acknowledges specific achievements and results customer has obtained'
    },
    {
      type: 'action',
      icon: Star,
      genieIcon: 'share',
      label: 'Requests testimonial',
      description: 'Asks if customer would share their experience to help others'
    },
    {
      type: 'action',
      icon: FileText,
      genieIcon: 'settings',
      label: 'Facilitates testimonial creation',
      description: 'Guides customer through creating testimonial with templates and assistance'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'smile',
      label: 'Marketing content created',
      description: 'Authentic testimonial captured for use in marketing materials and social proof'
    }
  ],
  metrics: {
    conversionRate: '29%',
    responseTime: '<1 second',
    satisfactionScore: '4.9/5',
    costSavings: '$30k/month',
    timeToValue: '5 minutes'
  },
  testimonials: [
    {
      companyName: 'SuccessMetrics Inc',
      authorName: 'Robert Chen',
      authorTitle: 'Marketing Director',
      quote: 'Automated testimonial harvesting generated 50+ customer success stories in 3 months. Our conversion rate improved 22%.',
      metrics: '50+ testimonials collected in 3 months'
    }
  ],
  resources: [
    {
      type: 'template',
      title: 'Testimonial Request Templates',
      description: 'Proven approaches for requesting and collecting customer testimonials',
      url: '/templates/testimonial-requests'
    },
    {
      type: 'guide',
      title: 'Success Metrics Tracking',
      description: 'How to identify the right moments to request testimonials from customers',
      url: '/docs/success-metrics-tracking'
    }
  ]
};